/**
 * Production Monitoring System
 * 
 * Comprehensive monitoring and observability with:
 * - Real-time performance monitoring
 * - Structured logging with context
 * - Error tracking and analytics
 * - User interaction monitoring
 * - Resource usage tracking
 * - Visual monitoring dashboard
 * - Automated alerting
 */

// Performance monitoring — import locally so MonitoringUtils/useMonitoring can reference them
// (replaces CommonJS require() which is unsupported in Vite ESM)
import {
  PerformanceMonitor,
  initializePerformanceMonitoring,
  getPerformanceMonitor,
  recordApiPerformance,
} from './PerformanceMonitor';
export { PerformanceMonitor, initializePerformanceMonitoring, getPerformanceMonitor, recordApiPerformance };

export type {
  PerformanceMetrics,
  AlertConfig,
  MonitoringConfig
} from './PerformanceMonitor';

// Logging system — import locally for the same reason
import { Logger, initializeLogger, getLogger, log } from './Logger';
export { Logger, initializeLogger, getLogger, log };

export type {
  LogLevel,
  LogEntry,
  LoggerConfig
} from './Logger';

// Monitoring dashboard
export { MonitoringDashboard } from './MonitoringDashboard';

// Monitoring integrates with existing FERP infrastructure
// Circuit breaker functionality provided by existing monitoring system

// Safety Metrics (if they exist)
// export { default as SafetyMetricsCollector } from './SafetyMetricsCollector';
// export { default as RealTimeSafetyMonitor } from './RealTimeSafetyMonitor';

// Monitoring utilities and helpers
export const MonitoringUtils = {
  /**
   * Initialize complete monitoring stack
   */
  initializeMonitoring: (config?: {
    performance?: Partial<import('./PerformanceMonitor').MonitoringConfig>;
    logging?: Partial<import('./Logger').LoggerConfig>;
  }) => {
    const performanceMonitor = initializePerformanceMonitoring(config?.performance);
    const logger = initializeLogger(config?.logging);
    
    // Log monitoring initialization
    logger.info('Monitoring system initialized', {
      performance: !!performanceMonitor,
      logging: !!logger,
      timestamp: new Date().toISOString()
    });
    
    return { performanceMonitor, logger };
  },

  /**
   * Create performance timing decorator
   */
  withPerformanceTracking: <T extends (...args: any[]) => Promise<any>>(
    fn: T,
    operationName: string
  ): T => {
    return (async (...args: any[]) => {
      const logger = getLogger();
      return logger.timeOperation(operationName, () => fn(...args));
    }) as T;
  },

  /**
   * Track user action with performance metrics
   */
  trackUserAction: (action: string, context?: Record<string, unknown>) => {
    const logger = getLogger();
    logger.info(`User action: ${action}`, {
      ...context,
      userAction: true,
      timestamp: Date.now()
    });
    
    // Record API performance if available
    const monitor = getPerformanceMonitor();
    if (monitor) {
      // This would be called by the actual API call
      // recordApiPerformance(responseTime, isError);
    }
  },

  /**
   * Create error context with monitoring data
   */
  createErrorContext: (additionalContext?: Record<string, unknown>) => {
    const monitor = getPerformanceMonitor();
    const metrics = monitor?.getMetrics();
    
    return {
      ...additionalContext,
      performance: metrics ? {
        memoryUsage: metrics.memory.usedHeapSize,
        coreWebVitals: metrics.coreWebVitals,
        sessionDuration: metrics.session.duration
      } : undefined,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };
  },

  /**
   * Get system health status
   */
  getSystemHealth: () => {
    const monitor = getPerformanceMonitor();
    const logger = getLogger();
    
    if (!monitor) {
      return { status: 'unknown', details: 'Monitoring not initialized' };
    }
    
    const metrics = monitor.getMetrics();
    const issues: string[] = [];
    
    // Check Core Web Vitals
    if (metrics.coreWebVitals.lcp && metrics.coreWebVitals.lcp > 2500) {
      issues.push('Poor LCP performance');
    }
    if (metrics.coreWebVitals.fid && metrics.coreWebVitals.fid > 100) {
      issues.push('High input delay');
    }
    if (metrics.coreWebVitals.cls && metrics.coreWebVitals.cls > 0.1) {
      issues.push('Layout instability');
    }
    
    // Check memory usage
    const memoryUsage = metrics.memory.usedHeapSize / metrics.memory.heapSizeLimit;
    if (memoryUsage > 0.8) {
      issues.push('High memory usage');
    }
    
    // Check error rate
    if (metrics.api.errorRate > 0.05) {
      issues.push('High error rate');
    }
    
    const status = issues.length === 0 ? 'healthy' : 
                   issues.length <= 2 ? 'warning' : 'critical';
    
    return {
      status,
      details: issues.length > 0 ? issues.join(', ') : 'All systems operational',
      metrics: {
        lcp: metrics.coreWebVitals.lcp,
        fid: metrics.coreWebVitals.fid,
        cls: metrics.coreWebVitals.cls,
        memoryUsage: Math.round(memoryUsage * 100),
        errorRate: Math.round(metrics.api.errorRate * 100),
        responseTime: Math.round(metrics.api.averageResponseTime)
      }
    };
  },

  /**
   * Export comprehensive monitoring report
   */
  exportMonitoringReport: async () => {
    const monitor = getPerformanceMonitor();
    const logger = getLogger();
    
    const report = {
      timestamp: new Date().toISOString(),
      systemHealth: MonitoringUtils.getSystemHealth(),
      performanceMetrics: monitor?.getMetrics(),
      recentLogs: logger.exportLogs(),
      environment: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        buildVersion: process.env.REACT_APP_VERSION || 'unknown',
        environment: process.env.NODE_ENV || 'unknown'
      }
    };
    
    return JSON.stringify(report, null, 2);
  }
};

// SAGA monitoring components - Domain-agnostic distributed transaction monitoring
export { SagaMonitoringDashboard } from './SagaMonitoringDashboard';
export { SagaAuditTrailViewer } from './SagaAuditTrailViewer';
export { SagaPrimitiveMetricsDashboard } from './SagaPrimitiveMetricsDashboard';
export { SagaCoordinationHealthMonitor } from './SagaCoordinationHealthMonitor';

// React hooks for monitoring
export const useMonitoring = () => {
  const logger = getLogger();
  const monitor = getPerformanceMonitor();

  const trackAction = (action: string, context?: Record<string, unknown>) => {
    MonitoringUtils.trackUserAction(action, context);
  };

  const trackPerformance = async <T>(
    operation: string,
    fn: () => Promise<T>,
    context?: Record<string, unknown>
  ): Promise<T> => {
    return logger.timeOperation(operation, fn, context);
  };

  const getSystemHealth = () => {
    return MonitoringUtils.getSystemHealth();
  };

  const createChildLogger = (defaultContext: Record<string, unknown>) => {
    return logger.createChildLogger(defaultContext);
  };

  return {
    trackAction,
    trackPerformance,
    getSystemHealth,
    createChildLogger,
    logger,
    monitor
  };
};